import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonList,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    sentence: string;
    options: string[];
    correctOption: string;
}


const QuestionWords: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctOption) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                if (currentIndex != questions.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setSelectedOption(null);
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'QuestionWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/QuestionWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Question Words Worksheets for UKG | Learn WH-Questions Easily"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help UKG kids learn and practice question words like Who, What, Where, and Why with engaging activities and worksheets at Mama’s Guidance.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Question Words</h1>

                                <p>
                                    <strong>Question words</strong> help children ask questions and seek information. Words like <strong>What, Where, Who, When, Why, and How</strong> form the basis of curiosity and communication. These worksheets are perfect for UKG kids to practice WH-questions in a fun and simple way.
                                </p>

                                <h2>❓ What Are Question Words?</h2>
                                <p>
                                    Question words are used to ask questions about people, places, time, reasons, and actions. For example:
                                    <ul>
                                        <li><strong>What</strong> is your name?</li>
                                        <li><strong>Where</strong> do you live?</li>
                                        <li><strong>Who</strong> is your teacher?</li>
                                        <li><strong>When</strong> is your birthday?</li>
                                        <li><strong>Why</strong> are you happy?</li>
                                        <li><strong>How</strong> are you feeling?</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and understand WH-question words</li>
                                    <li>Match questions with suitable answers</li>
                                    <li>Practice asking and answering simple questions</li>
                                    <li>Improve communication and language skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Match the question word to its answer</li>
                                    <li>Circle the correct question word in a sentence</li>
                                    <li>Fill in the blanks with suitable WH-words</li>
                                    <li>Picture-based questions (<strong>e.g., Who is playing?</strong>)</li>
                                    <li>Sort questions by type (<strong>Who/What/Where</strong>)</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Encourage children to ask questions during playtime</li>
                                    <li>Read picture books and ask WH-questions about the story</li>
                                    <li>Use real-life examples like <strong>“Where is the spoon?” or “Why are we going out?”</strong></li>
                                    <li>Model full-sentence answers using question words</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Let your child be curious! When they ask questions, answer patiently — and ask them questions too. That’s how conversation grows.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Question Words</h1>

                            <p>
                                <strong>Question words</strong> help children ask questions and seek information. Words like <strong>What, Where, Who, When, Why, and How</strong> form the basis of curiosity and communication. These worksheets are perfect for UKG kids to practice WH-questions in a fun and simple way.
                            </p>

                            <h2>❓ What Are Question Words?</h2>
                            <p>
                                Question words are used to ask questions about people, places, time, reasons, and actions. For example:
                                <ul>
                                    <li><strong>What</strong> is your name?</li>
                                    <li><strong>Where</strong> do you live?</li>
                                    <li><strong>Who</strong> is your teacher?</li>
                                    <li><strong>When</strong> is your birthday?</li>
                                    <li><strong>Why</strong> are you happy?</li>
                                    <li><strong>How</strong> are you feeling?</li>
                                </ul>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and understand WH-question words</li>
                                <li>Match questions with suitable answers</li>
                                <li>Practice asking and answering simple questions</li>
                                <li>Improve communication and language skills</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Match the question word to its answer</li>
                                <li>Circle the correct question word in a sentence</li>
                                <li>Fill in the blanks with suitable WH-words</li>
                                <li>Picture-based questions (<strong>e.g., Who is playing?</strong>)</li>
                                <li>Sort questions by type (<strong>Who/What/Where</strong>)</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Encourage children to ask questions during playtime</li>
                                <li>Read picture books and ask WH-questions about the story</li>
                                <li>Use real-life examples like <strong>“Where is the spoon?” or “Why are we going out?”</strong></li>
                                <li>Model full-sentence answers using question words</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Let your child be curious! When they ask questions, answer patiently — and ask them questions too. That’s how conversation grows.
                            </p>
                        </article>
                        <h2>Fill in the Blanks with Correct Question Words</h2>

                        <p>
                            <strong>
                                Q. {questions[currentIndex]?.sentence}
                            </strong>
                        </p>
                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value)}
                        >
                            {questions[currentIndex]?.options.map((option, index) => (
                                <IonItem
                                    className={` ${selectedOption === option
                                        ? option === questions[currentIndex]?.correctOption
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonLabel>{option}</IonLabel>
                                    <IonRadio slot="start" value={option} />
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default QuestionWords;
